/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
import { queueMicrotask } from '../ponyfills';
import { IQueueStorage } from './types';
import { QueueStatus } from './constants';
import { Task, ITaskJSON } from './Task';

/**
 * An abstract class to create storage objects for the Queue.
 */
export abstract class QueueStorage<T> implements IQueueStorage<T> {
    /**
     * Creates a QueueStorage object with some options.
     *
     * @param maxLength Maximum allowed length per value.
     */
    constructor(protected maxLength: number = 1048) {}

    /**
     * Get all pending tasks from the store.
     *
     * @param name Name of the queue that created the tasks.
     * @returns An array pending tasks.
     */
    public getTasks(name: string): Task<T>[] {
        const tasks = this.get<ITaskJSON<T>[]>(`queue:${name}:tasks`, []);
        return tasks.map(task => Task.fromJSON(task));
    }

    /**
     * Store pending tasks.
     *
     * @param name Name of the queue the tasks belong to.
     * @param tasks The tasks to store.
     */
    public setTasks(name: string, tasks: Task<T>[]): void {
        queueMicrotask(() => {
            this.set(`queue:${name}:tasks`, tasks);
        });
    }

    /**
     * Get all failed tasks from the store.
     *
     * @param name The name of the queue.
     * @returns An array of failed tasks.
     */
    public getFailed(name: string): Task<T>[] {
        const tasks = this.get<ITaskJSON<T>[]>(`queue:${name}:failed`, []);
        return tasks.map(task => Task.fromJSON(task));
    }

    /**
     * Store failed tasks.
     *
     * @param name The name of the queue.
     * @param tasks Failed tasks to store.
     */
    public setFailed(name: string, tasks: Task<T>[]): void {
        queueMicrotask(() => {
            this.set(`queue:${name}:failed`, tasks);
        });
    }

    /**
     * Get the status of a queue.
     *
     * @param name Name of the queue.
     */
    public getStatus(name: string): QueueStatus {
        return this.get(`queue:${name}:status`, QueueStatus.IDLE);
    }

    /**
     * Store the status of a queue.
     *
     * @param name Name of the queue.
     * @param status The status of the queue.
     */
    public setStatus(name: string, status: QueueStatus): void {
        queueMicrotask(() => {
            this.set(`queue:${name}:status`, status);
        });
    }

    /**
     * Retrieve a value from storage.
     *
     * @param key Key of the value.
     * @param defaultValue Value to return when the key is not found.
     * @returns The stored value, parsed as a T type.
     */
    protected abstract get<T>(key: string, defaultValue: T): T;
    /**
     * Put a value in storage.
     *
     * @param key The key of the value.
     * @param value The value.
     */
    protected abstract set(key: string, value: unknown): void;
}



// WEBPACK FOOTER //
// ./src/packages/core/lib/queue/QueueStorage.ts